import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Zap, Lightbulb, Shield, Target, Users, Building2, Rocket, TrendingUp, Award, Clock, Brain, Quote, CheckCircle, Handshake, Star, Sparkles, BookOpen, Workflow, Code, Cloud, Crown } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface QuiEstBZModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuiEstBZModal({ isOpen, onClose }: QuiEstBZModalProps) {
  const [activeTab, setActiveTab] = useState<'valeurs' | 'mission' | 'histoire' | 'approche' | 'equipe'>('valeurs');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['qui_est_bz_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  const valeurs = [
    {
      id: 1,
      nom: 'Respect',
      icone: Heart,
      couleur: 'from-red-500 to-pink-500',
      description: 'Par notre transparence, notre écoute active des commentaires clients et notre sens de la responsabilisation.',
      exemples: [
        'Transparence totale dans nos communications avec les clients',
        'Écoute active des besoins et feedbacks pour améliorer continuellement',
        'Responsabilisation de chaque membre de l\'équipe dans ses décisions',
        'Respect des engagements et des délais convenus',
        'Reconnaissance de la valeur de chaque membre de l\'équipe'
      ],
      citations: [
        {
          auteur: 'Dany Bourassa',
          texte: 'Le respect, c\'est être transparent avec nos clients. Pas de surprises. Si on peut pas livrer, on le dit. Si on peut faire mieux, on le fait. C\'est ça, le respect authentique.',
          avatar: '/DB.png'
        },
        {
          auteur: 'Dany Bourassa',
          texte: 'Respecter nos clients, c\'est les écouter vraiment. Pas juste entendre, mais comprendre leurs enjeux et leurs objectifs. C\'est la base d\'un partenariat durable.',
          avatar: '/DB.png'
        },
        {
          auteur: 'François Delisle',
          texte: 'Le respect technique, c\'est livrer ce qu\'on promet, au niveau de qualité qu\'on promet, dans les délais qu\'on promet. Pas de compromis sur l\'excellence.',
          avatar: '/FP.png'
        }
      ],
      impact: 'Le respect crée la confiance. La confiance crée les partenariats durables.'
    },
    {
      id: 2,
      nom: 'Disponibilité',
      icone: Zap,
      couleur: 'from-blue-500 to-cyan-500',
      description: 'Par notre capacité à réagir rapidement et par l\'accès simplifié à un humain pour nos clients.',
      exemples: [
        'Support technique disponible 7h00 à 17h00, du lundi au vendredi',
        'Accès direct à un humain, pas de labyrinthe téléphonique',
        'Réactivité rapide face aux urgences client',
        'Clavardage web en temps réel pour support immédiat',
        'Présence constante pour nos clients, même en dehors des heures normales'
      ],
      citations: [
        {
          auteur: 'Dany Bourassa',
          texte: 'Disponibilité, c\'est être là quand le client en a besoin. Pas quand c\'est pratique pour nous. Nos clients ont des urgences, on répond. Point.',
          avatar: '/DB.png'
        },
        {
          auteur: 'Dany Bourassa',
          texte: 'La disponibilité, c\'est l\'engagement qu\'un client peut toujours joindre quelqu\'un qui peut l\'aider. Pas une boîte vocale. Un humain qui comprend.',
          avatar: '/DB.png'
        },
        {
          auteur: 'François Delisle',
          texte: 'Disponibilité technique, c\'est avoir les bonnes personnes au bon moment. Notre équipe est structurée pour ça. On répond, on résout, on avance.',
          avatar: '/FP.png'
        }
      ],
      impact: 'La disponibilité transforme les clients en partenaires. On est là, toujours.'
    },
    {
      id: 3,
      nom: 'Innovation',
      icone: Lightbulb,
      couleur: 'from-purple-500 to-indigo-500',
      description: 'Par l\'intégration de solutions technologiques innovantes et par notre avant-gardisme.',
      exemples: [
        'Intégration d\'IA dans nos processus pour améliorer la productivité',
        'Adoption précoce des nouvelles technologies (PRIsME créé en 10 jours)',
        'Recherche constante de solutions innovantes pour nos clients',
        'Formation continue de l\'équipe sur les dernières tendances',
        'Développement de solutions propriétaires (PRIsME, TactiCE)'
      ],
      citations: [
        {
          auteur: 'Dany Bourassa',
          texte: 'L\'innovation, c\'est pas juste utiliser les nouvelles technos. C\'est créer des solutions qui n\'existaient pas avant. PRIsME, c\'est ça. On invente l\'avenir.',
          avatar: '/DB.png'
        },
        {
          auteur: 'Dany Bourassa',
          texte: 'Innovation commerciale, c\'est trouver de nouvelles façons de créer de la valeur pour nos clients. PRIsME est notre différenciateur majeur.',
          avatar: '/DB.png'
        },
        {
          auteur: 'François Delisle',
          texte: 'L\'innovation technique, c\'est choisir les bonnes technologies au bon moment. Pas suivre les modes, mais identifier ce qui crée vraiment de la valeur.',
          avatar: '/FP.png'
        }
      ],
      impact: 'L\'innovation nous positionne comme leaders, pas suiveurs.'
    },
    {
      id: 4,
      nom: 'Rigueur',
      icone: Shield,
      couleur: 'from-green-500 to-emerald-500',
      description: 'Par nos produits et services de grande qualité et par notre engagement envers l\'excellence.',
      exemples: [
        'Documentation exhaustive de chaque projet et intervention',
        'Standards de qualité non-négociables pour tous les livrables',
        'Processus structurés et reproductibles (méthodologie PRIsME)',
        'Contrôle qualité rigoureux avant livraison client',
        'Engagement envers l\'excellence dans chaque détail'
      ],
      citations: [
        {
          auteur: 'Dany Bourassa',
          texte: 'La rigueur, c\'est faire les choses bien, pas juste les faire. Chaque livrable doit être excellent. Pas "assez bon". Excellent.',
          avatar: '/DB.png'
        },
        {
          auteur: 'Dany Bourassa',
          texte: 'Rigueur commerciale, c\'est tenir nos promesses. Si on dit qu\'on va livrer X dans Y temps, on livre X dans Y temps. Toujours.',
          avatar: '/DB.png'
        },
        {
          auteur: 'François Delisle',
          texte: 'La rigueur technique, c\'est la qualité mesurable. Code propre, architecture solide, documentation complète. C\'est non-négociable.',
          avatar: '/FP.png'
        }
      ],
      impact: 'La rigueur garantit l\'excellence. L\'excellence garantit la confiance.'
    }
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-[95vw] h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-8">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="relative flex items-center gap-6">
              <div className="bg-white/20 backdrop-blur-xl p-5 rounded-2xl">
                <Building2 className="w-12 h-12 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-4xl font-black text-white">Qui est BZ</h1>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-purple-100 text-lg font-semibold">Les valeurs, la mission, la vision et l'ADN de BZ Inc.</p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-purple-200">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex gap-2 px-8 pt-6 border-b border-gray-200 bg-gray-50">
            <button
              onClick={() => setActiveTab('valeurs')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all rounded-t-lg ${
                activeTab === 'valeurs'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Heart className="w-5 h-5" />
              <span>Les 4 Valeurs</span>
            </button>
            <button
              onClick={() => setActiveTab('mission')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all rounded-t-lg ${
                activeTab === 'mission'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Target className="w-5 h-5" />
              <span>Mission & Vision</span>
            </button>
            <button
              onClick={() => setActiveTab('histoire')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all rounded-t-lg ${
                activeTab === 'histoire'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              <span>Notre Histoire</span>
            </button>
            <button
              onClick={() => setActiveTab('approche')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all rounded-t-lg ${
                activeTab === 'approche'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Rocket className="w-5 h-5" />
              <span>Notre Approche</span>
            </button>
            <button
              onClick={() => setActiveTab('equipe')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all rounded-t-lg ${
                activeTab === 'equipe'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>L'Équipe BZ</span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8">
            
            {/* ONGLET 1: LES 4 VALEURS */}
            {activeTab === 'valeurs' && (
              <div className="space-y-8 max-w-6xl mx-auto">
                {/* Introduction */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-8">
                  <div className="text-center">
                    <h2 className="text-3xl font-black text-gray-900 mb-4">Les 4 Valeurs Fondamentales de BZ</h2>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Ces valeurs ne sont pas des mots sur un mur. Ce sont des principes vécus quotidiennement par chaque membre de l'équipe BZ. 
                      Elles guident nos décisions, façonnent notre culture et définissent qui nous sommes.
                    </p>
                  </div>
                </div>

                {/* Les 4 Valeurs */}
                <div className="grid md:grid-cols-2 gap-6">
                  {valeurs.map((valeur) => {
                    const Icon = valeur.icone;
                    return (
                      <motion.div
                        key={valeur.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: valeur.id * 0.1 }}
                        className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all"
                      >
                        {/* Header Valeur */}
                        <div className={`bg-gradient-to-br ${valeur.couleur} text-white rounded-xl p-6 mb-6`}>
                          <div className="flex items-center gap-4 mb-3">
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                              <Icon className="w-8 h-8" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-black">{valeur.nom}</h3>
                              <p className="text-white/90 text-sm mt-1">{valeur.description}</p>
                            </div>
                          </div>
                        </div>

                        {/* Exemples Concrets */}
                        <div className="mb-6">
                          <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            Comment on vit cette valeur
                          </h4>
                          <ul className="space-y-2">
                            {valeur.exemples.map((exemple, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-gray-700">
                                <span className="text-purple-500 mt-1">•</span>
                                <span>{exemple}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Citations des Experts */}
                        <div className="space-y-4 mb-6">
                          <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <Quote className="w-5 h-5 text-purple-600" />
                            Paroles d'Experts
                          </h4>
                          {valeur.citations.map((citation, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-500">
                              <div className="flex items-start gap-3 mb-2">
                                <img 
                                  src={citation.avatar} 
                                  alt={citation.auteur}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                                <div className="flex-1">
                                  <p className="font-bold text-gray-900">{citation.auteur}</p>
                                  <p className="text-gray-700 italic text-sm mt-1">"{citation.texte}"</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Impact */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4">
                          <p className="text-green-900 font-bold text-center">
                            💡 {valeur.impact}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Message Final */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 text-center">
                  <h3 className="text-2xl font-black mb-4">Ces valeurs nous définissent</h3>
                  <p className="text-lg leading-relaxed">
                    Respect, Disponibilité, Innovation, Rigueur. Quatre mots simples. Une promesse complexe. 
                    C'est l'ADN de BZ Inc. C'est qui nous sommes. C'est ce qui nous guide chaque jour.
                  </p>
                </div>
              </div>
            )}

            {/* ONGLET 2: MISSION & VISION */}
            {activeTab === 'mission' && (
              <div className="space-y-8 max-w-5xl mx-auto">
                {/* Mission */}
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-8 border-2 border-blue-300">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="bg-blue-500 rounded-full p-4">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-3xl font-black text-gray-900 mb-4">Notre Mission</h2>
                      <p className="text-xl text-gray-800 leading-relaxed font-semibold mb-4">
                        Prendre en charge les opérations TI des entreprises québécoises avec des solutions durables, efficientes, 
                        à la fine pointe de la technologie, axées sur la sécurité tout en favorisant le succès d'affaires de nos clients.
                      </p>
                      <div className="bg-white/50 rounded-xl p-6 mt-6">
                        <h3 className="font-bold text-gray-900 mb-3">Ce que ça signifie concrètement:</h3>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Solutions durables:</strong> On ne fait pas du quick fix. On bâtit pour le long terme.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Fine pointe technologique:</strong> On reste à jour, on innove, on adopte ce qui crée de la valeur.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Sécurité axée:</strong> La sécurité n'est pas optionnelle. C'est au cœur de tout ce qu'on fait.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Succès d'affaires:</strong> On ne fait pas juste de la TI. On fait de la TI qui fait grandir nos clients.</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vision */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-8 border-2 border-purple-300">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="bg-purple-500 rounded-full p-4">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-3xl font-black text-gray-900 mb-4">Notre Vision</h2>
                      <p className="text-xl text-gray-800 leading-relaxed font-semibold mb-4">
                        Être un acteur de la transformation numérique des entreprises québécoises en les inspirant et en les accompagnant 
                        dans leurs opérations courantes et dans l'atteinte de leurs objectifs. Nous sommes déterminés à voir prospérer nos clients.
                      </p>
                      <div className="bg-white/50 rounded-xl p-6 mt-6">
                        <h3 className="font-bold text-gray-900 mb-3">Notre engagement:</h3>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start gap-2">
                            <Star className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Inspirer:</strong> On montre ce qui est possible. On ouvre les horizons.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Star className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Accompagner:</strong> On ne laisse pas nos clients seuls. On est là, à chaque étape.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Star className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Faire prospérer:</strong> Le succès de nos clients, c'est notre succès. On est déterminés.</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Citations Vision */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border-2 border-orange-200">
                    <div className="flex items-center gap-3 mb-4">
                      <img src="/DB.png" alt="Dany" className="w-12 h-12 rounded-full object-cover" />
                      <div>
                        <p className="font-bold text-gray-900">Dany Bourassa</p>
                        <p className="text-sm text-gray-600">Président</p>
                      </div>
                    </div>
                    <Quote className="w-6 h-6 text-orange-500 mb-2" />
                    <p className="text-gray-700 italic">
                      "Notre mission, c'est pas juste gérer de la TI. C'est transformer des entreprises québécoises. 
                      On veut voir nos clients prospérer. C'est ça, notre raison d'être."
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border-2 border-red-200">
                    <div className="flex items-center gap-3 mb-4">
                      <img src="/DB.png" alt="Dany" className="w-12 h-12 rounded-full object-cover" />
                      <div>
                        <p className="font-bold text-gray-900">Dany Bourassa</p>
                        <p className="text-sm text-gray-600">CA/CE</p>
                      </div>
                    </div>
                    <Quote className="w-6 h-6 text-red-500 mb-2" />
                    <p className="text-gray-700 italic">
                      "La vision BZ, c'est être le partenaire stratégique qui transforme. Pas juste un fournisseur. 
                      Un vrai partenaire qui veut voir ses clients réussir."
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
                    <div className="flex items-center gap-3 mb-4">
                      <img src="/FP.png" alt="François" className="w-12 h-12 rounded-full object-cover" />
                      <div>
                        <p className="font-bold text-gray-900">François Delisle</p>
                        <p className="text-sm text-gray-600">VP Technologies</p>
                      </div>
                    </div>
                    <Quote className="w-6 h-6 text-blue-500 mb-2" />
                    <p className="text-gray-700 italic">
                      "Notre vision technique, c'est livrer de l'excellence qui transforme. Des solutions qui ne sont pas juste 
                      fonctionnelles, mais qui créent vraiment de la valeur pour nos clients."
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 3: NOTRE HISTOIRE */}
            {activeTab === 'histoire' && (
              <div className="space-y-8 max-w-5xl mx-auto">
                {/* Introduction */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-8">
                  <h2 className="text-3xl font-black text-gray-900 mb-4 text-center">Qui Sommes-Nous?</h2>
                  <p className="text-lg text-gray-700 text-center leading-relaxed">
                    BZ est un guichet unique qui vous accompagne dans l'ensemble de vos besoins TI. 
                    Nous mettons l'accent sur l'aspect relationnel dans nos engagements, c'est pourquoi nous cherchons des partenariats durables avec nos clients.
                  </p>
                </div>

                {/* Notre Approche Relationnelle */}
                <div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
                  <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                    <Handshake className="w-8 h-8 text-blue-600" />
                    L'Approche Relationnelle BZ
                  </h3>
                  <div className="space-y-4 text-gray-700">
                    <p className="text-lg leading-relaxed">
                      <strong className="text-gray-900">Nous sommes reconnus dans l'industrie comme étant toujours présents et à tout moment pour nos clients.</strong>
                    </p>
                    <p className="leading-relaxed">
                      Notre priorité est de considérer votre organisation dans son ensemble, en tenant compte à la fois de votre modèle d'affaires 
                      et de vos orientations futures. Grâce à notre approche, nous sommes en mesure de vous présenter une stratégie optimale en fonction 
                      de vos besoins et de votre budget.
                    </p>
                    <p className="leading-relaxed">
                      Notre équipe multidisciplinaire se dévoue pour accompagner les membres de votre organisation dans la planification et dans l'exécution 
                      de vos projets, assurant ainsi une prise en charge efficace, complète et fluide.
                    </p>
                  </div>
                </div>

                {/* Notre Expertise */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-8">
                  <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                    <Award className="w-8 h-8 text-green-600" />
                    25 Ans d'Excellence TI
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-3">Notre Expertise</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Plus de 60 employés regroupés dans 6 départements spécialisés</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Une équipe de plus de 25 techniciens et experts qualifiés</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Une quinzaine de gestionnaires chevronnés</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>25 ans d'expérience en transformation TI</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-3">Notre Différenciation</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>Approche holistique qui comprend votre entreprise dans son ensemble</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>Solutions durables, sécuritaires et adaptées</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>Partenariats durables, pas des transactions ponctuelles</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>Accès direct à un humain, toujours</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Le Slogan */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 text-center">
                  <h3 className="text-3xl font-black mb-4">On s'occupe de tout de B à Z</h3>
                  <p className="text-xl mb-2">L'étape A, c'est de nous contacter!</p>
                  <p className="text-lg opacity-90">
                    Contactez l'un de nos experts pour obtenir plus d'informations sur la solution qui convient le mieux à vos besoins.
                  </p>
                </div>
              </div>
            )}

            {/* ONGLET 4: NOTRE APPROCHE */}
            {activeTab === 'approche' && (
              <div className="space-y-8 max-w-5xl mx-auto">
                {/* Introduction */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-2xl p-8">
                  <h2 className="text-3xl font-black text-gray-900 mb-4 text-center">Notre Approche Holistique</h2>
                  <p className="text-lg text-gray-700 text-center leading-relaxed">
                    Notre approche holistique nous permet de comprendre votre entreprise dans son ensemble. 
                    Notre expertise en technologies de l'information combinée avec notre approche nous permet de déployer 
                    des solutions durables, sécuritaires et adaptées.
                  </p>
                </div>

                {/* Les 3 Axes */}
                <div>
                  <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">Notre approche est basée sur 3 axes</h3>
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border-2 border-blue-300">
                      <div className="bg-blue-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-xl font-black text-gray-900 mb-3 text-center">Humain</h4>
                      <p className="text-gray-700 text-center">
                        L'humain au centre. Comprendre les besoins, les défis et les aspirations de chaque membre de votre organisation.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border-2 border-purple-300">
                      <div className="bg-purple-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Workflow className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-xl font-black text-gray-900 mb-3 text-center">Processus</h4>
                      <p className="text-gray-700 text-center">
                        Structurer et optimiser vos processus pour maximiser l'efficacité et la productivité.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border-2 border-green-300">
                      <div className="bg-green-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Zap className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-xl font-black text-gray-900 mb-3 text-center">Technologie</h4>
                      <p className="text-gray-700 text-center">
                        Solutions technologiques à la fine pointe, sécuritaires et adaptées à vos besoins spécifiques.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Les 4 Piliers */}
                <div>
                  <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">et s'articule autour de quatre piliers</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border-2 border-orange-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-orange-500 rounded-full w-12 h-12 flex items-center justify-center">
                          <Target className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="text-xl font-black text-gray-900">Planification</h4>
                      </div>
                      <p className="text-gray-700">
                        Optimiser et mettre à jour votre stratégie de manière continue et proactive. 
                        Plan directeur stratégique 1 à 5 ans selon vos besoins.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-xl p-6 border-2 border-blue-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-blue-500 rounded-full w-12 h-12 flex items-center justify-center">
                          <Shield className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="text-xl font-black text-gray-900">Gestion</h4>
                      </div>
                      <p className="text-gray-700">
                        Gérer vos opérations TI tout en assurant une veille technologique constante. 
                        Infogérance flexible, agile et proactive.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-xl p-6 border-2 border-green-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center">
                          <Handshake className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="text-xl font-black text-gray-900">Accompagnement</h4>
                      </div>
                      <p className="text-gray-700">
                        Implanter les mesures requises à l'exécution de votre stratégie globale. 
                        Accompagnement personnalisé par un directeur de compte et un conseiller TI.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border-2 border-purple-300">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-purple-500 rounded-full w-12 h-12 flex items-center justify-center">
                          <Zap className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="text-xl font-black text-gray-900">Soutien</h4>
                      </div>
                      <p className="text-gray-700">
                        Recommander des solutions efficientes et analyser exhaustivement votre environnement, 
                        vos besoins, vos objectifs et vos capacités.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Les 5 Étapes */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-8">
                  <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">Notre approche en 5 étapes</h3>
                  <div className="space-y-4">
                    {[
                      { num: 1, titre: 'Optimiser et mettre à jour', desc: 'Votre stratégie de manière continue et proactive' },
                      { num: 2, titre: 'Gérer vos opérations TI', desc: 'Tout en assurant une veille technologique constante' },
                      { num: 3, titre: 'Implanter les mesures requises', desc: 'À l\'exécution de votre stratégie globale' },
                      { num: 4, titre: 'Recommander des solutions efficientes', desc: 'En fonction de vos objectifs d\'affaires' },
                      { num: 5, titre: 'Analyser exhaustivement', desc: 'Votre environnement, vos besoins, vos objectifs et vos capacités' }
                    ].map((etape, idx) => (
                      <div key={idx} className="flex items-start gap-4 bg-white rounded-xl p-5 border-2 border-gray-200">
                        <div className="bg-indigo-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold flex-shrink-0">
                          {etape.num}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1">{etape.titre}</h4>
                          <p className="text-gray-700">{etape.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 5: L'ÉQUIPE BZ */}
            {activeTab === 'equipe' && (
              <div className="space-y-8 max-w-6xl mx-auto">
                {/* Introduction */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-8">
                  <h2 className="text-3xl font-black text-gray-900 mb-4 text-center">L'Équipe BZ</h2>
                  <p className="text-lg text-gray-700 text-center leading-relaxed">
                    Plus de 60 employés regroupés dans 6 départements spécialisés. Une équipe de plus de 25 techniciens et experts qualifiés. 
                    Une quinzaine de gestionnaires chevronnés. Voici les leaders qui portent BZ vers l'excellence.
                  </p>
                </div>

                {/* Top 3 */}
                <div>
                  <h3 className="text-2xl font-black text-gray-900 mb-6 text-center flex items-center justify-center gap-3">
                    <Crown className="w-8 h-8 text-yellow-500" />
                    Le Top 3 Leadership BZ
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      {
                        nom: 'Dany Bourassa',
                        titre: 'Président Directeur Général',
                        avatar: '/DB.png',
                        couleur: 'from-orange-500 to-red-500',
                        email: 'dany.bourassa@bzinc.ca',
                        role: 'Vision stratégique et leadership pour transformer vos défis TI en opportunités de croissance.'
                      },
                      {
                        nom: 'Dany Bourassa',
                        titre: 'Vice-Président Stratégie et Développement',
                        avatar: '/DB.png',
                        couleur: 'from-red-500 to-pink-500',
                        email: 'dany.bourassa@bzinc.ca',
                        role: 'Développement d\'affaires et stratégies innovantes pour propulser votre organisation.'
                      },
                      {
                        nom: 'François Delisle',
                        titre: 'Vice-Président TI et Cybersécurité',
                        avatar: '/FP.png',
                        couleur: 'from-blue-500 to-cyan-500',
                        email: 'francois.delisle@bzinc.ca',
                        role: 'Excellence technologique et innovation pour concrétiser vos ambitions numériques.'
                      }
                    ].map((leader, idx) => (
                      <div key={idx} className={`bg-gradient-to-br ${leader.couleur} text-white rounded-2xl p-6`}>
                        <div className="flex flex-col items-center text-center">
                          <img 
                            src={leader.avatar} 
                            alt={leader.nom}
                            className="w-24 h-24 rounded-full border-4 border-white/30 mb-4 object-cover"
                          />
                          <h4 className="text-xl font-black mb-1">{leader.nom}</h4>
                          <p className="text-white/90 text-sm mb-3">{leader.titre}</p>
                          <p className="text-white/80 text-sm mb-4">{leader.email}</p>
                          <p className="text-white/90 text-sm leading-relaxed">{leader.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Les Départements */}
                <div>
                  <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">Nos 6 Départements</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { nom: 'Service & Direction TI', icon: Target, bgGradient: 'from-blue-50 to-blue-100', border: 'border-blue-200', iconBg: 'bg-blue-500' },
                      { nom: 'Ventes & Marketing', icon: TrendingUp, bgGradient: 'from-red-50 to-red-100', border: 'border-red-200', iconBg: 'bg-red-500' },
                      { nom: 'Cybersécurité', icon: Shield, bgGradient: 'from-purple-50 to-purple-100', border: 'border-purple-200', iconBg: 'bg-purple-500' },
                      { nom: 'Programmation & IA', icon: Code, bgGradient: 'from-indigo-50 to-indigo-100', border: 'border-indigo-200', iconBg: 'bg-indigo-500' },
                      { nom: 'Infonuagique', icon: Cloud, bgGradient: 'from-cyan-50 to-cyan-100', border: 'border-cyan-200', iconBg: 'bg-cyan-500' },
                      { nom: 'Administration & RH', icon: Users, bgGradient: 'from-orange-50 to-orange-100', border: 'border-orange-200', iconBg: 'bg-orange-500' }
                    ].map((dept, idx) => {
                      const Icon = dept.icon;
                      return (
                        <div key={idx} className={`bg-gradient-to-br ${dept.bgGradient} rounded-xl p-5 border-2 ${dept.border}`}>
                          <div className="flex items-center gap-3">
                            <div className={`${dept.iconBg} rounded-lg p-2`}>
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <h4 className="font-bold text-gray-900">{dept.nom}</h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Message Final */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 text-center">
                  <h3 className="text-2xl font-black mb-4">Une Équipe Unie par l'Excellence</h3>
                  <p className="text-lg leading-relaxed">
                    Chaque membre de l'équipe BZ partage les mêmes valeurs: Respect, Disponibilité, Innovation, Rigueur. 
                    Ensemble, on transforme l'industrie TI. Ensemble, on fait la différence.
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
